import type { PostfachKanal } from "./postfach-logik";

/**
 * Lucide-Symbole des Postfachs (R-F: Lucide, Inline-SVG; das Bausteinpaket führt keine Icon-Abhängigkeit).
 * Alle dekorativ (`aria-hidden`); der Name steht im Text daneben.
 */
function Zeichen({ children, klasse = "shrink-0 size-4" }: { children: React.ReactNode; klasse?: string }) {
  return (
    <>
      {/* Preline: component/badge/badge-soft-color-variants (Symbol vor dem Text; Lucide als Inline-SVG, R-F) */}
      <svg className={klasse} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
    </>
  );
}

/** Lucide `Inbox`. */
export function PostfachSymbol({ klasse }: { klasse?: string }) {
  return (
    <Zeichen klasse={klasse}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </Zeichen>
  );
}

/** Lucide `ChevronLeft` (zurück zur Liste, mobil). */
export function ZurueckSymbol() {
  return (
    <Zeichen>
      <path d="m15 18-6-6 6-6" />
    </Zeichen>
  );
}

/** Lucide `Trash2`. */
export function PapierkorbSymbol() {
  return (
    <Zeichen>
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </Zeichen>
  );
}

/** Lucide `X`. */
export function SchliessenX() {
  return (
    <Zeichen>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Zeichen>
  );
}

/** Lucide `Mail`, `Calendar`, `MessageCircle`, `Smartphone`, `Bell`, `Webhook` je Kanal. */
export function KanalSymbol({ kanal }: { kanal: PostfachKanal }) {
  switch (kanal) {
    case "mail":
      return (
        <Zeichen klasse="shrink-0 size-3">
          <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
          <rect x="2" y="4" width="20" height="16" rx="2" />
        </Zeichen>
      );
    case "kalender":
      return (
        <Zeichen klasse="shrink-0 size-3">
          <path d="M8 2v4" />
          <path d="M16 2v4" />
          <rect width="18" height="18" x="3" y="4" rx="2" />
          <path d="M3 10h18" />
        </Zeichen>
      );
    case "teams":
      return (
        <Zeichen klasse="shrink-0 size-3">
          <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
        </Zeichen>
      );
    case "push":
      return (
        <Zeichen klasse="shrink-0 size-3">
          <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
          <path d="M12 18h.01" />
        </Zeichen>
      );
    case "glocke":
      return (
        <Zeichen klasse="shrink-0 size-3">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </Zeichen>
      );
    case "webhook":
      return (
        <Zeichen klasse="shrink-0 size-3">
          <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" />
          <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" />
          <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" />
        </Zeichen>
      );
  }
}

/** Lucide `ExternalLink` (Link öffnet in neuem Tab). */
export function ExternSymbol() {
  return (
    <Zeichen>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </Zeichen>
  );
}

/** Lucide `PictureInPicture2` („Abkoppeln“: Postfach in eigenes Fenster). */
export function AbkoppelnSymbol() {
  return (
    <Zeichen>
      <path d="M21 9V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v10c0 1.1.9 2 2 2h4" />
      <rect width="10" height="7" x="12" y="13" rx="2" />
    </Zeichen>
  );
}

/** Lucide `RefreshCw` („Aktualisieren“). */
export function AktualisierenSymbol() {
  return (
    <Zeichen>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </Zeichen>
  );
}

/** Lucide `Link` (Link ins Portal, selber Tab). */
export function LinkSymbol() {
  return (
    <Zeichen>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Zeichen>
  );
}
